import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import PageWrapper from './common/PageWrapper';
import { WordPressPage, WordPressSponsor, wordpressApi } from '../services/wordpress';
import './Info.css';
import { useLocalization } from '../localization';

const getSponsorImage = (sponsor: WordPressSponsor) => {
  const featuredImage = sponsor._embedded?.['wp:featuredmedia']?.[0];
  if (featuredImage) {
    return {
      src: featuredImage.source_url,
      alt: featuredImage.alt_text || sponsor.title.rendered.replace(/<[^>]*>/g, ''),
    };
  }

  const imageTag = sponsor.content?.rendered.match(/<img\b[^>]*>/i)?.[0];
  const source = imageTag?.match(/\bsrc=["']([^"']+)["']/i)?.[1];
  if (!source) return null;

  return {
    src: source,
    alt: imageTag?.match(/\balt=["']([^"']*)["']/i)?.[1] || sponsor.title.rendered.replace(/<[^>]*>/g, ''),
  };
};

const Projecte: React.FC = () => {
  const { copy, projectPageSlug } = useLocalization();
  const [page, setPage] = useState<WordPressPage | null>(null);
  const [sponsors, setSponsors] = useState<WordPressSponsor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadPage = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setPage(null);

        const result = await wordpressApi.getPageBySlug(projectPageSlug);

        if (!isCancelled) {
          setPage(result);
        }
      } catch (_error) {
        if (!isCancelled) {
          setErrorMessage(copy.project.loadError);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadPage();

    return () => {
      isCancelled = true;
    };
  }, [copy.project.loadError, projectPageSlug]);

  useEffect(() => {
    let isCancelled = false;

    const loadSponsors = async () => {
      try {
        const result = await wordpressApi.getSponsors();

        if (!isCancelled) {
          setSponsors(result);
        }
      } catch (_error) {
        // Sponsors are supplementary content, so the project page remains available if this request fails.
      }
    };

    void loadSponsors();

    return () => {
      isCancelled = true;
    };
  }, []);

  const pageTitle = page?.title?.rendered || copy.project.fallbackTitle;
  const pageContent = page?.content?.rendered || '';
  const sponsorImages = sponsors
    .map((sponsor) => ({ sponsor, image: getSponsorImage(sponsor) }))
    .filter((item): item is { sponsor: WordPressSponsor; image: NonNullable<ReturnType<typeof getSponsorImage>> } => Boolean(item.image));

  return (
    <Container>
      <PageWrapper title={pageTitle} containerClassName="page-container-complete-border">
        <div className="info-page">
          <section className="info-block" aria-labelledby="projecte-title">
            <h3 id="projecte-title" className="info-heading">
              {pageTitle}
            </h3>
            {isLoading ? (
              <p>{copy.project.loading}</p>
            ) : null}
            {!isLoading && errorMessage ? (
              <p>{errorMessage}</p>
            ) : null}
            {!isLoading && !errorMessage && !page ? (
              <p>{copy.project.notFound}</p>
            ) : null}
            {!isLoading && page ? (
              <div
                className="info-content"
                dangerouslySetInnerHTML={{ __html: pageContent }}
              />
            ) : null}
          </section>
          {sponsorImages.length ? (
            <section className="info-block sponsors-block" aria-labelledby="sponsors-title">
              <h3 id="sponsors-title" className="info-heading">{copy.project.sponsors}</h3>
              <div className="sponsors-grid">
                {sponsorImages.map(({ sponsor, image }) => (
                  <article className="sponsor-card" key={sponsor.id}>
                    <img src={image.src} alt={image.alt} />
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </PageWrapper>
    </Container>
  );
};

export default Projecte;
